import localforage from "localforage";

export interface StorageBackend {
  getItem<T>(key: string): Promise<T | null>;
  setItem<T>(key: string, value: T): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

class LocalForageBackend implements StorageBackend {
  private readonly instance: LocalForage;

  constructor(name: string, storeName: string) {
    this.instance = localforage.createInstance({
      name,
      storeName,
    });
  }

  async getItem<T>(key: string): Promise<T | null> {
    return await this.instance.getItem<T>(key);
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    await this.instance.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    await this.instance.removeItem(key);
  }

  async clear(): Promise<void> {
    await this.instance.clear();
  }
}

class MemoryBackend implements StorageBackend {
  private readonly store = new Map<string, unknown>();

  async getItem<T>(key: string): Promise<T | null> {
    return (this.store.get(key) as T | undefined) ?? null;
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    this.store.set(key, value);
  }

  async removeItem(key: string): Promise<void> {
    this.store.delete(key);
  }

  async clear(): Promise<void> {
    this.store.clear();
  }
}

function hasWebStorage(): boolean {
  try {
    return (
      typeof window !== "undefined" && typeof window.indexedDB !== "undefined"
    );
  } catch {
    return false;
  }
}

export class StorageService {
  constructor(
    private readonly backend: StorageBackend = hasWebStorage()
      ? new LocalForageBackend("DevAdventure2K", "game_saves")
      : new MemoryBackend()
  ) {}

  async get<T>(key: string): Promise<T | null> {
    try {
      return await this.backend.getItem<T>(key);
    } catch (err) {
      console.warn("[storage] read failed for", key, err);
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    try {
      await this.backend.setItem(key, value);
    } catch (err) {
      console.warn("[storage] write failed for", key, err);
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await this.backend.removeItem(key);
    } catch (err) {
      console.warn("[storage] remove failed for", key, err);
    }
  }

  async clear(): Promise<void> {
    try {
      await this.backend.clear();
    } catch (err) {
      console.warn("[storage] clear failed", err);
    }
  }
}
